import { useFormContext } from 'react-hook-form';
import { CurrencyInput, FormField, MoneyInput } from '@percent/lemonade';
import { CurrencyCode, Money, currencyCodes } from '@percent/utility';
import get from 'lodash/get';
import { useTranslation } from 'react-i18next';
import { MoneyQuestion as MoneyQuestionType } from '@percent/grants-nomination-form/api/nominationForm/nominationForm.types';
import { DEFAULT_OPERATING_BUDGET_CURRENCY } from '@percent/grants-nomination-form/constants/operatingBudget';
import { formatKey } from '@percent/grants-nomination-form/utils/formKey';
import { computeMoneyUpdate, MoneyInputValue } from './computeMoneyUpdate';

const ALL_CURRENCIES: CurrencyCode[] = [...currencyCodes];

type MoneyQuestionProps = { question: MoneyQuestionType };

export const MoneyQuestion = ({ question }: MoneyQuestionProps) => {
  const { t, i18n } = useTranslation();
  const { setValue, getValues, formState } = useFormContext();

  const amountValueRaw = getValues(question.amountKey);
  const currencyValueRaw = question.currencyKey ? getValues(question.currencyKey) : undefined;

  const currencies = question.allowedCurrencies?.length ? question.allowedCurrencies : ALL_CURRENCIES;
  const defaultCurrency = question.defaultCurrency ?? DEFAULT_OPERATING_BUDGET_CURRENCY;

  const currency: CurrencyCode = (currencyValueRaw as CurrencyCode | undefined) ?? defaultCurrency;

  const amountMinor = amountValueRaw != null && amountValueRaw !== '' ? Number(amountValueRaw) : undefined;
  const moneyValue: Money | undefined =
    amountMinor !== undefined && !Number.isNaN(amountMinor) ? { amount: amountMinor, currency } : undefined;

  const formattedAmountKey = formatKey(question.amountKey);
  const touched = get(formState.touchedFields, formattedAmountKey);
  const error = touched ? get(formState.errors, formattedAmountKey) : undefined;

  const writeAmount = (minor: number | undefined) => {
    setValue(question.amountKey, minor === undefined ? '' : String(minor), {
      shouldValidate: true,
      shouldTouch: true,
    });
  };

  const writeCurrency = (next: CurrencyCode) => {
    if (question.currencyKey) {
      setValue(question.currencyKey, next, { shouldValidate: true, shouldTouch: true });
    }
  };

  const handleValueChange = (value: MoneyInputValue, raw: string | undefined) => {
    const update = computeMoneyUpdate(value, raw);
    switch (update.kind) {
      case 'clear':
        writeAmount(undefined);
        return;
      case 'currency-only':
        writeCurrency(update.currency);
        return;
      case 'ignore':
        return;
      case 'commit':
        writeAmount(update.amountMinor);
        writeCurrency(update.currency);
        return;
    }
  };

  const sharedInputProps = {
    id: question.amountKey,
    name: question.amountKey,
    locale: i18n.language || 'en-US',
    value: moneyValue,
    placeholder: { amount: 0, currency },
    allowNegativeValue: false,
    onValueChange: handleValueChange,
  };

  return (
    <FormField
      label={question.label}
      status={error ? 'danger' : 'default'}
      necessity={question.amountRequired ? 'required' : 'optional'}
      optionalText={t('typography.optional')}
      statusMessage={error ? (error as { message?: string }).message?.toString() : undefined}
      description={question.description}
      instructions={question.instructions}
      data-testid={question.amountKey}
    >
      {question.currencyKey ? (
        <MoneyInput defaultCurrency={currency} currencies={currencies} {...sharedInputProps} />
      ) : (
        <CurrencyInput currency={currency} {...sharedInputProps} />
      )}
    </FormField>
  );
};
